import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormatUtils from 'utils/format';
import SpotUtils from 'utils/spot';
import Image from 'common/image/Image';
import withConfig from 'config/withConfig';
import StarRatingWithCategory from 'common/star-rating/StarRatingWithCategory';
import WalkingDistanceDisplay from 'spot-details/map/WalkingDistanceDisplay';

class TransientMonthlyFacilityDetails extends Component {
    static propTypes = {
        spot: PropTypes.object.isRequired,
        isMonthly: PropTypes.bool,
        imageWidth: PropTypes.number,
        imageHeight: PropTypes.number,
        staticBaseUrl: PropTypes.string,
        showPrice: PropTypes.bool,
        showImages: PropTypes.bool,
        showFullTitle: PropTypes.bool,
        showGarageType: PropTypes.bool,
        showAccessHours: PropTypes.bool,
        showAmenities: PropTypes.bool,
    };
    static defaultProps = {
        imageWidth: 75,
        imageHeight: 75,
        showPrice: true,
        showImages: true,
        showFullTitle: false,
        // CR-9704: Nik requested keeping garage type, access hours, and amenities code for reuse in spot details
        showGarageType: false,
        showAccessHours: false,
        showAmenities: false,
    };

    constructor(props) {
        super(props);

        const {
            imageWidth,
            imageHeight,
            spot: {facility, title},
            staticBaseUrl,
        } = props;
        const firstImage = !isUndefined(facility.images[0])
            ? facility.images[0]
            : null;

        this._imgProps = {
            width: imageWidth,
            height: imageHeight,
            itemProp: 'image',
            alt: `Photo of ${title}`,
            lazyLoad: true,
            ...(firstImage
                ? {cloudinaryTemplate: firstImage.url_template}
                : {src: `${staticBaseUrl}img/missing-image.png`}),
        };
    }

    _renderPrice = () => {
        const {
            spot: {
                selectedRate: {
                    display_price: displayPrice,
                    currency_type: currencyType,
                },
            },
        } = this.props;
        const displayPriceFormatted = SpotUtils.formatRatePrice({
            price: displayPrice,
            currencyType,
        });

        return (
            <div
                className="price"
                dangerouslySetInnerHTML={{__html: displayPriceFormatted}}
            />
        );
    };

    _renderOfferSchema = () => {
        const {
            spot: {
                selectedRate: {
                    duration,
                    display_price: displayPriceRaw,
                    starts,
                    ends,
                    fullUrl,
                    currency_type: currencyType,
                },
                title,
            },
        } = this.props;
        const {displayPrice} = FormatUtils.price(displayPriceRaw);
        const description = `Reserved Parking at ${title} for ${duration} Hour${
            duration === 1 ? '' : 's'
        }`;

        return (
            <Fragment>
                <meta itemProp="name" content="Parking" />
                <meta itemProp="url" content={fullUrl} />
                <meta itemProp="description" content={description} />
                <meta itemProp="priceCurrency" content={currencyType} />
                <meta itemProp="price" content={displayPrice} />
                <meta
                    itemProp="availability"
                    content="http://schema.org/InStock"
                />
                <meta itemProp="validFrom" content={starts} />
                <meta itemProp="validThrough" content={ends} />
                <span
                    itemProp="offeredBy"
                    itemScope
                    itemType="http://schema.org/Organization"
                >
                    <meta itemProp="name" content="SpotHero" />
                    <meta itemProp="url" content="https://spothero.com" />
                </span>
            </Fragment>
        );
    };

    _renderSpotSchema = () => {
        const {
            spot: {
                facility: {
                    facility_url: spotUrl,
                    street_address: address,
                    city,
                    state,
                },
                latitude,
                longitude,
            },
        } = this.props;

        return (
            <Fragment>
                <span
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                >
                    <meta itemProp="streetAddress" content={address} />
                    <meta itemProp="addressLocality" content={city} />
                    <meta itemProp="addressRegion" content={state} />
                </span>
                <span
                    itemProp="geo"
                    itemScope
                    itemType="http://schema.org/GeoCoordinates"
                >
                    <meta itemProp="latitude" content={latitude} />
                    <meta itemProp="longitude" content={longitude} />
                </span>
                <span
                    itemProp="containedInPlace"
                    itemScope
                    itemType="http://schema.org/City"
                >
                    <meta itemProp="name" content={city} />
                    <meta
                        itemProp="url"
                        content={spotUrl.split('-parking')[0]}
                    />
                </span>
            </Fragment>
        );
    };

    _renderTitle() {
        const {
            spot: {title},
            showFullTitle,
            showPrice,
        } = this.props;
        const titleClasses = classNames(
            'TransientMonthlyFacilityDetails-title',
            {'show-full-title': showFullTitle},
            {'hide-price': !showPrice}
        );

        return <p className={titleClasses}>{title}</p>;
    }

    _filterSlugs(source, slugs) {
        return source
            .filter(item => Boolean(slugs.find(slug => item.slug === slug)))
            .map(item => item.name);
    }

    _renderList(items) {
        if (!items.length) {
            return null;
        }

        return (
            <ul className="TransientMonthlyFacilityDetails-list">
                {items.map((type, i) => {
                    const line = i > 0 ? `· ${type}` : type;

                    return <li key={type}>{line}</li>;
                })}
            </ul>
        );
    }

    _renderParkingType() {
        const {
            spot: {
                facility: {facility_type: facilityType},
                selectedRate: {amenities},
            },
        } = this.props;
        const slugs = ['self-park', 'valet'];
        let types = null;

        if (!isEmpty(facilityType)) {
            types = this._filterSlugs(amenities, slugs);
            types.push(facilityType.display_name);
        } else {
            slugs.push('covered-parking');

            types = this._filterSlugs(amenities, slugs);

            // transient amenity is "Covered", monthly is "Covered Parking", fun!
            if (
                !types.includes('Covered') &&
                !types.includes('Covered Parking')
            ) {
                types.push('Uncovered');
            }
        }

        return this._renderList(types);
    }

    _renderHoursAndAmenities() {
        const {
            spot: {
                facility: {
                    hours_of_operation: {text},
                },
                selectedRate: {amenities},
            },
        } = this.props;
        const {showAccessHours, showAmenities} = this.state;
        const items = [];

        if (showAccessHours && text[0] === 'This facility is open 24/7.') {
            items.push('Open 24/7');
        }

        if (showAmenities) {
            const slugs = ['attendant', 'in-out'];
            const activeAmenities = this._filterSlugs(amenities, slugs);

            items.push(...activeAmenities.reverse());
        }

        return this._renderList(items);
    }

    render() {
        const {
            spot: {
                selectedRate,
                distance,
                distanceInMiles,
                walkingDuration,
                facility: {
                    rating_info: {
                        number_of_ratings: numberOfReviews,
                        star_rating: starRating,
                    },
                },
            },
            isMonthly,
            showPrice,
            showImages,
            showGarageType,
            showAccessHours,
            showAmenities,
        } = this.props;
        const {unavailable_reason: unavailableReason} = selectedRate;
        const classes = classNames('TransientMonthlyFacilityDetails', {
            'TransientMonthlyFacilityDetails-hide-images': !showImages,
        });

        return (
            <div className={classes}>
                {showImages && (
                    <div className="images">
                        <Image {...this._imgProps} />
                    </div>
                )}
                <div className="detail">
                    <h2 className="heading-md">
                        <span itemProp="name">
                            {this._renderTitle()}
                            <StarRatingWithCategory
                                numberOfReviews={numberOfReviews}
                                starRating={starRating}
                            />
                            <WalkingDistanceDisplay
                                distance={distance}
                                distanceInMiles={distanceInMiles}
                                walkingDuration={walkingDuration}
                            />
                            {showGarageType && this._renderParkingType()}
                            {(showAccessHours || showAmenities) &&
                                this._renderHoursAndAmenities()}
                        </span>
                    </h2>
                    {this._renderSpotSchema()}
                    {unavailableReason && (
                        <span className="not-available-reason text-light">
                            <sup>*</sup> {unavailableReason}
                        </span>
                    )}
                </div>
                {showPrice && (
                    <div className="TransientMonthlyFacilityDetails-price-container">
                        {this._renderPrice()}
                        <span>{isMonthly ? '/month' : 'total'}</span>
                    </div>
                )}
            </div>
        );
    }
}

export default withConfig(['staticBaseUrl'])(TransientMonthlyFacilityDetails);
